import React, { useEffect, useMemo } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";

/**
 * OrderDetailsModal
 * A self-contained, drop-in order/receipt detail view.
 *
 * Props:
 *  - open      : boolean
 *  - onClose   : () => void
 *  - cart      : the flattened cart object (from allCarts) — includes `session`, `produits`, etc.
 *  - currency  : string (optional, e.g. "TND")
 *  - t         : i18n translate fn (optional)
 *  - onPrint   : () => void (optional)
 *
 * Styles are scoped under `.odm` and injected once, so this won't collide
 * with your AdminLTE / Bootstrap theme.
 */

const STYLE_ID = "odm-styles";

const CSS = `
.odm-wrap { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
.odm {
  --ink:#15161c; --sub:#7a7d89; --line:#ededf2; --paper:#ffffff;
  --accent:#1f7a5a; --soft:#f7f8fa;
  --paid-bg:#e9f7ef; --paid-fg:#18794e;
  --pend-bg:#fdf3e6; --pend-fg:#9a6206;
  --due:#b42318;
  background: var(--paper); color: var(--ink);
  border-radius: 16px; overflow: hidden;
  box-shadow: 0 24px 60px -20px rgba(20,22,40,.35), 0 4px 14px rgba(20,22,40,.08);
}
.odm * { box-sizing: border-box; }

/* Header */
.odm-head { padding: 22px 24px 18px; border-bottom: 1px solid var(--line); position: relative; }
.odm-code {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  font-size: 20px; font-weight: 700; letter-spacing: .3px; color: var(--ink); margin: 0;
}
.odm-head-meta { margin-top: 6px; font-size: 13px; color: var(--sub); }
.odm-head-meta b { color: var(--ink); font-weight: 600; }
.odm-status {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 700; letter-spacing: .2px;
  padding: 6px 12px; border-radius: 999px;
}
.odm-status.paid { background: var(--paid-bg); color: var(--paid-fg); }
.odm-status.pending { background: var(--pend-bg); color: var(--pend-fg); }
.odm-status.canceled { background: #fdecea; color: var(--due); }
.odm-status .dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.odm-head-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.odm-close {
  position: absolute; top: 16px; right: 16px;
  width: 32px; height: 32px; border-radius: 8px; border: none;
  background: var(--soft); color: var(--sub); cursor: pointer;
  font-size: 16px; line-height: 1; display: grid; place-items: center; transition: .15s;
}
.odm-close:hover { background: #eef0f3; color: var(--ink); }

/* Chips */
.odm-chips { display: flex; flex-wrap: wrap; gap: 8px; padding: 16px 24px 4px; }
.odm-chip {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; color: var(--sub); background: var(--soft);
  padding: 6px 11px; border-radius: 8px;
}
.odm-chip b { color: var(--ink); font-weight: 600; }
.odm-chip.tag { background: #eef4ff; color: #2256c8; font-weight: 600; }

/* Items */
.odm-section-label {
  padding: 18px 24px 8px; font-size: 11px; font-weight: 700; letter-spacing: .8px;
  text-transform: uppercase; color: var(--sub);
}
.odm-items { padding: 0 24px; }
.odm-item { display: flex; align-items: center; gap: 13px; padding: 12px 0; border-bottom: 1px solid var(--line); }
.odm-item:last-child { border-bottom: none; }
.odm-thumb {
  width: 46px; height: 46px; border-radius: 10px; flex: 0 0 auto;
  object-fit: contain; background: var(--soft); border: 1px solid var(--line);
}
.odm-thumb-fallback {
  width: 46px; height: 46px; border-radius: 10px; flex: 0 0 auto;
  display: grid; place-items: center; background: var(--soft); border: 1px solid var(--line);
  font-weight: 700; color: var(--sub); font-size: 16px;
}
.odm-item-info { flex: 1 1 auto; min-width: 0; }
.odm-item-name { font-size: 14px; font-weight: 600; color: var(--ink); line-height: 1.3; }
.odm-item-sub {
  font-size: 12px; color: var(--sub); margin-top: 3px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.odm-item-amt { text-align: right; flex: 0 0 auto; }
.odm-item-total {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 14px; font-weight: 700; color: var(--ink); white-space: nowrap;
}
.odm-item-remise { font-size: 11px; color: var(--paid-fg); margin-top: 2px; white-space: nowrap; }

/* Receipt summary */
.odm-summary { margin: 20px 24px 0; padding: 18px 18px 20px; background: var(--soft); border-radius: 12px; position: relative; }
.odm-summary::before {
  content: ""; position: absolute; left: 0; right: 0; top: -1px; height: 2px;
  background-image: repeating-linear-gradient(90deg, var(--line) 0 7px, transparent 7px 13px);
}
.odm-row { display: flex; justify-content: space-between; align-items: baseline; padding: 5px 0; font-size: 13.5px; color: var(--sub); }
.odm-row span:last-child {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: var(--ink); font-weight: 600;
}
.odm-row.discount span:last-child { color: var(--paid-fg); }
.odm-total {
  display: flex; justify-content: space-between; align-items: baseline;
  margin-top: 10px; padding-top: 12px; border-top: 1px dashed #d7dae1;
}
.odm-total .lbl { font-size: 14px; font-weight: 700; color: var(--ink); }
.odm-total .val {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 22px; font-weight: 800; color: var(--accent); letter-spacing: -.3px;
}
.odm-pay { display: flex; justify-content: space-between; align-items: baseline; padding: 4px 0; font-size: 13px; color: var(--sub); margin-top: 4px; }
.odm-pay span:last-child { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; color: var(--ink); font-weight: 600; }
.odm-pay.balance.due span:last-child { color: var(--due); }
.odm-pay.balance.change span:last-child { color: var(--paid-fg); }

/* Payments */
.odm-pays { margin: 16px 24px 0; }
.odm-payrow {
  display: flex; align-items: center; gap: 12px;
  padding: 11px 14px; background: var(--soft); border-radius: 10px; margin-bottom: 8px;
}
.odm-payrow:last-child { margin-bottom: 0; }
.odm-pay-ic {
  width: 34px; height: 34px; flex: 0 0 auto; border-radius: 9px;
  display: grid; place-items: center; background: #fff; color: var(--accent);
  border: 1px solid var(--line); font-size: 14px;
}
.odm-pay-meta { flex: 1 1 auto; min-width: 0; }
.odm-pay-method { font-size: 13.5px; font-weight: 600; color: var(--ink); }
.odm-pay-rendu { font-size: 11.5px; color: var(--sub); margin-top: 2px; }
.odm-pay-amt {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 14px; font-weight: 700; color: var(--ink); white-space: nowrap;
}

/* Footer */
.odm-foot { display: flex; gap: 10px; padding: 18px 24px 22px; }
.odm-btn {
  flex: 1; border: none; border-radius: 10px; padding: 11px 14px;
  font-size: 13.5px; font-weight: 600; cursor: pointer; transition: .15s;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
}
.odm-btn-print { background: var(--accent); color: #fff; }
.odm-btn-print:hover { filter: brightness(1.06); }
.odm-btn-close { background: var(--soft); color: var(--ink); }
.odm-btn-close:hover { background: #eef0f3; }

@media print {
  .odm-close, .odm-foot { display: none !important; }
  .odm { box-shadow: none; }
}
`;

const BOX_SX = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 560,
  maxWidth: "94vw",
  maxHeight: "92vh",
  overflowY: "auto",
  bgcolor: "transparent",
  outline: "none",
  border: "none",
  boxShadow: "none",
};

function money(n, currency) {
  const v = Number(n || 0).toFixed(3);
  return currency ? `${v} ${currency}` : v;
}

// Maps raw payment_method values to a label key + FontAwesome icon.
const PAYMENT_METHODS = {
  cash: { label: "Espèces", icon: "fa-money-bill-wave" },
  espece: { label: "Espèces", icon: "fa-money-bill-wave" },
  especes: { label: "Espèces", icon: "fa-money-bill-wave" },
  liquide: { label: "Espèces", icon: "fa-money-bill-wave" },
  card: { label: "Carte", icon: "fa-credit-card" },
  carte: { label: "Carte", icon: "fa-credit-card" },
  cb: { label: "Carte", icon: "fa-credit-card" },
  tpe: { label: "Carte", icon: "fa-credit-card" },
  cheque: { label: "Chèque", icon: "fa-money-check" },
  virement: { label: "Virement", icon: "fa-building-columns" },
  ticket: { label: "Ticket resto", icon: "fa-ticket" },
  ticket_restaurant: { label: "Ticket resto", icon: "fa-ticket" },
};

function paymentInfo(method) {
  const key = String(method || "")
    .toLowerCase()
    .trim();
  return (
    PAYMENT_METHODS[key] || {
      label: method ? String(method) : "Paiement",
      icon: "fa-wallet",
    }
  );
}

export default function OrderDetailsModal({
  open,
  onClose,
  cart,
  currency = "",
  t,
  onPrint,
}) {
  const tr = useMemo(() => (typeof t === "function" ? t : (s) => s), [t]);

  // Inject scoped styles once
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.innerHTML = CSS;
    document.head.appendChild(el);
  }, []);

  const data = useMemo(() => {
    if (!cart) return null;
    const session = cart.session || {};
    const items = (cart.produits || []).map((it) => {
      const product = it.product || {};
      const qty = Number(it.quantity ?? 1);
      const unit = Number(it.price ?? product.prix_ttc ?? 0);
      const gross = Number(it.totalPrice ?? unit * qty);
      const remise = Number(it.remise ?? 0);
      const net = Number(it.netPrice ?? gross - remise);
      return {
        id: it._id || product._id,
        name:
          product?.titre?.fr ||
          product?.titre?.en ||
          product?.titre?.ar ||
          tr("Produit sans nom"),
        image: Array.isArray(product.image) ? product.image[0] : product.image,
        qty,
        unit,
        gross,
        remise,
        net,
      };
    });

    const itemsGross = items.reduce((s, i) => s + i.gross, 0);
    const remiseTotal = items.reduce((s, i) => s + i.remise, 0);
    const timbre = Number(session.timbre || 0);
    const total = Number(
      session.total_amount ?? cart.prix ?? itemsGross - remiseTotal + timbre,
    );

    // Payments attached to the session (payment_method, amount, rendu)
    const rawPayments = cart.payments || session.payments || [];
    const payments = rawPayments.map((p) => ({
      method: p.payment_method,
      amount: Number(p.amount || 0),
      rendu: Number(p.rendu || 0),
    }));

    const paid = Number(
      session.paid_amount ??
        (payments.length
          ? payments.reduce((s, p) => s + p.amount - p.rendu, 0)
          : 0),
    );
    const balance = +(total - paid).toFixed(3);

    const isPaid =
      cart.status === "Payée" ||
      session.status === "inactive" ||
      (paid > 0 && balance <= 0);

    const isCanceled =
      cart.status === "Annulée" || session.status === "canceled";

    const d = cart.createdAt || session.createdAt;
    let dateStr = "—";
    if (d) {
      const dt = new Date(d);
      const pad = (n) => String(n).padStart(2, "0");
      const day = pad(dt.getDate());
      const month = pad(dt.getMonth() + 1);
      const year = dt.getFullYear();
      const hours = pad(dt.getHours());
      const minutes = pad(dt.getMinutes());
      dateStr = `${day}-${month}-${year} ${hours}:${minutes}`;
    }

    const cashier = session.caissier_id;
    const server = session.serveur_id;

    return {
      session,
      items,
      payments,
      itemsGross,
      remiseTotal,
      timbre,
      total,
      paid,
      balance,
      isPaid,
      isCanceled,
      dateStr,
      cashierName: cashier?.nom
        ? `${cashier.nom} ${cashier.prenom || ""}`.trim()
        : null,
      serverName: server?.nom
        ? `${server.nom} ${server.prenom || ""}`.trim()
        : null,
      table:
        session.table_id?.table_number ??
        session.table_id?.numero ??
        cart.tableNumber ??
        "—",
      takeAway: !!cart.takeAway,
      code: cart.code || session.code || "—",
    };
  }, [cart, tr]);

  const handlePrint = () => {
    if (!data) return;

    const esc = (s) =>
      String(s ?? "").replace(
        /[&<>]/g,
        (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]),
      );

    const line = (left, right, opts = {}) => `
      <div class="line${opts.cls ? " " + opts.cls : ""}">
        <span>${esc(left)}</span><span class="amt">${esc(right)}</span>
      </div>`;

    const itemsHtml = data.items
      .map(
        (it) => `
        <div class="item">
          <div class="item-name">${esc(it.name)}</div>
          <div class="line">
            <span>${it.qty} x ${esc(money(it.unit, currency))}</span>
            <span class="amt">${esc(money(it.net, currency))}</span>
          </div>
          ${
            it.remise > 0
              ? `<div class="line muted"><span>${tr(
                  "Remise",
                )}</span><span class="amt">-${esc(
                  money(it.remise, currency),
                )}</span></div>`
              : ""
          }
        </div>`,
      )
      .join("");

    const paymentsHtml = data.payments.length
      ? `<div class="sep"></div>
         <div class="section-title">${tr("Paiements")}</div>` +
        data.payments
          .map((p) => {
            const info = paymentInfo(p.method);
            return (
              line(tr(info.label), money(p.amount, currency)) +
              (p.rendu > 0
                ? `<div class="line muted"><span>${tr(
                    "Rendu",
                  )}</span><span class="amt">${esc(
                    money(p.rendu, currency),
                  )}</span></div>`
                : "")
            );
          })
          .join("")
      : "";

    const balanceHtml =
      Math.abs(data.balance) > 0.001
        ? line(
            data.balance > 0 ? tr("Reste à payer") : tr("Monnaie"),
            money(Math.abs(data.balance), currency),
          )
        : "";

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(
      data.code,
    )}</title><style>
      @page { margin: 6mm; }
      * { box-sizing: border-box; }
      body {
        font-family: "Courier New", ui-monospace, monospace;
        color: #000; margin: 0 auto; padding: 8px 0;
        width: 280px; font-size: 12px; line-height: 1.45;
      }
      .center { text-align: center; }
      .code { font-size: 16px; font-weight: bold; letter-spacing: 1px; }
      .meta { font-size: 11px; }
      .status { font-weight: bold; margin-top: 4px; }
      .sep { border-top: 1px dashed #000; margin: 8px 0; }
      .section-title { font-weight: bold; font-size: 11px; text-transform: uppercase; margin-bottom: 4px; }
      .item { margin-bottom: 6px; }
      .item-name { font-weight: bold; }
      .line { display: flex; justify-content: space-between; gap: 8px; }
      .line .amt { white-space: nowrap; }
      .muted { color: #444; font-size: 11px; }
      .total { font-size: 15px; font-weight: bold; margin: 4px 0; }
      .foot { text-align: center; margin-top: 10px; font-size: 11px; }
    </style></head><body>
      <div class="center">
        <div class="code">${esc(data.code)}</div>
        <div class="meta">${esc(data.dateStr)}</div>
        <div class="meta">${tr("Table")} ${esc(String(data.table))}${
          data.takeAway ? " &middot; " + tr("À emporter") : " &middot; " + tr("Sur place")
        }</div>
        ${
          data.cashierName
            ? `<div class="meta">${tr("Caissier")}: ${esc(data.cashierName)}</div>`
            : ""
        }
        ${
          data.serverName
            ? `<div class="meta">${tr("Serveur")}: ${esc(data.serverName)}</div>`
            : ""
        }
      </div>
      <div class="sep"></div>
      ${itemsHtml}
      <div class="sep"></div>
      ${line(tr("Sous-total"), money(data.itemsGross, currency))}
      ${data.remiseTotal > 0 ? line(tr("Remise"), "-" + money(data.remiseTotal, currency)) : ""}
      ${data.timbre > 0 ? line(tr("Timbre"), money(data.timbre, currency)) : ""}
      <div class="line total"><span>${tr("Total")}</span><span class="amt">${esc(
        money(data.total, currency),
      )}</span></div>
      ${line(tr("Payé"), money(data.paid, currency))}
      ${balanceHtml}
      ${paymentsHtml}
      <div class="sep"></div>
      <div class="foot center status">${data.isPaid ? tr("Payée") : tr("En cours")}</div>
      <div class="foot">${tr("Merci de votre visite")}</div>
    </body></html>`;

    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    const cleanup = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    const win = iframe.contentWindow;
    win.onafterprint = cleanup;
    setTimeout(() => {
      win.focus();
      win.print();
      setTimeout(cleanup, 2000);
    }, 200);
  };

  if (!data) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="order-details-modal"
      disableAutoFocus
    >
      <Box sx={BOX_SX} className="odm-wrap">
        <div className="odm">
          {/* Header */}
          <div className="odm-head">
            <button
              className="odm-close"
              aria-label={tr("Fermer")}
              onClick={onClose}
            >
              ✕
            </button>
            <div className="odm-head-top">
              <div>
                <h2 id="order-details-modal" className="odm-code">
                  {data.code}
                </h2>
                <div className="odm-head-meta">{data.dateStr}</div>
              </div>
              {/* <span
                className={`odm-status ${data.isPaid ? "paid" : "pending"}`}
              >
                <span className="dot" />
                {data.isPaid ? tr("Payée") : tr("En cours")}
              </span> */}
              {data.isCanceled && (
                <span className="odm-status canceled">
                  <span className="dot" />
                  {tr("Annulée")}
                </span>
              )}
            </div>
          </div>

          {/* Meta chips */}
          <div className="odm-chips">
            <span className="odm-chip">
              {data.table === 0 ? (
                <>
                  {tr("Table")} <b>{data.table}</b>
                </>
              ) : (
                <b>{tr("Comptoir")}</b>
              )}
            </span>
            {data.cashierName && (
              <span className="odm-chip">
                {tr("Caissier")} <b>{data.cashierName}</b>
              </span>
            )}
            {data.serverName && (
              <span className="odm-chip">
                {tr("Serveur")} <b>{data.serverName}</b>
              </span>
            )}
            <span className="odm-chip tag">
              {data.takeAway ? tr("À emporter") : tr("Sur place")}
            </span>
          </div>

          {/* Items */}
          <div className="odm-section-label">
            {tr("Articles")} · {data.items.length}
          </div>
          <div className="odm-items">
            {data.items.map((item, i) => (
              <div className="odm-item" key={item.id || i}>
                {item.image ? (
                  <img
                    className="odm-thumb"
                    src={item.image}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="odm-thumb-fallback">
                    {(item.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="odm-item-info">
                  <div className="odm-item-name">{item.name}</div>
                  <div className="odm-item-sub">
                    {item.qty} × {money(item.unit, currency)}
                  </div>
                </div>
                <div className="odm-item-amt">
                  <div className="odm-item-total">
                    {money(item.net, currency)}
                  </div>
                  {item.remise > 0 && (
                    <div className="odm-item-remise">
                      −{money(item.remise, currency)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Payment summary */}
          <div className="odm-summary">
            <div className="odm-row">
              <span>{tr("Sous-total")}</span>
              <span>{money(data.itemsGross, currency)}</span>
            </div>
            {data.remiseTotal > 0 && (
              <div className="odm-row discount">
                <span>{tr("Remise")}</span>
                <span>−{money(data.remiseTotal, currency)}</span>
              </div>
            )}
            {data.timbre > 0 && (
              <div className="odm-row">
                <span>{tr("Timbre")}</span>
                <span>{money(data.timbre, currency)}</span>
              </div>
            )}
            <div className="odm-total">
              <span className="lbl">{tr("Total")}</span>
              <span className="val">{money(data.total, currency)}</span>
            </div>
            <div className="odm-pay">
              <span>{tr("Payé")}</span>
              <span>{money(data.paid, currency)}</span>
            </div>
            {Math.abs(data.balance) > 0.001 && (
              <div
                className={`odm-pay balance ${
                  data.balance > 0 ? "due" : "change"
                }`}
              >
                <span>
                  {data.balance > 0 ? tr("Reste à payer") : tr("Monnaie")}
                </span>
                <span>{money(Math.abs(data.balance), currency)}</span>
              </div>
            )}
          </div>

          {/* Payments */}
          {data.payments.length > 0 && (
            <>
              <div className="odm-section-label">{tr("Paiements")}</div>
              <div className="odm-pays">
                {data.payments.map((p, i) => {
                  const info = paymentInfo(p.method);
                  return (
                    <div className="odm-payrow" key={i}>
                      <div className="odm-pay-ic">
                        <i className={`fa-solid ${info.icon}`} />
                      </div>
                      <div className="odm-pay-meta">
                        <div className="odm-pay-method">{tr(info.label)}</div>
                        {p.rendu > 0 && (
                          <div className="odm-pay-rendu">
                            {tr("Rendu")} : {money(p.rendu, currency)}
                          </div>
                        )}
                      </div>
                      <span className="odm-pay-amt">
                        {money(p.amount, currency)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Footer */}
          <div className="odm-foot">
            <button className="odm-btn odm-btn-close" onClick={onClose}>
              {tr("Fermer")}
            </button>
            <button
              id="printButton"
              className="odm-btn odm-btn-print"
              onClick={handlePrint}
            >
              <i className="fa-solid fa-print" /> {tr("Imprimer")}
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
}